import React from 'react';
import { Dialog, DialogTitle, DialogActions, DialogContentText, IconButton, Box } from '@mui/material';
import { useSettingsStore } from '@core/store';
import { useTranslation } from 'react-i18next';
import { WcnButton } from '@/components';
import { useStore } from '@core/store';

const StatusDialog = () => {
    const { darkMode, isMobile } = useSettingsStore();
    const { t } = useTranslation();

    const statusDialog = useStore((s) => s.statusDialog);
    const closeStatusDialog = useStore((s) => s.closeStatusDialog);

    const { open, status, heading, message, closable, actions } = statusDialog;

    const handleClose = () => {
        if (!closable) return;
        closeStatusDialog();
    };

    const handleConfirm = () => {
        closeStatusDialog();
        actions?.onConfirm?.();
    };

    const handleCancel = () => {
        closeStatusDialog();
        actions?.onCancel?.();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    width: isMobile ? undefined : '480px',
                    maxWidth: isMobile ? 'calc(100vw - 32px)' : '480px',
                    margin: isMobile ? 2 : 3,
                    backgroundColor: darkMode ? '#444444' : '#ffffff',
                    color: darkMode ? '#ffffff' : '#000000',
                    border: '1px solid #D9D9D9',
                    minHeight: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-evenly',
                    alignItems: 'center',
                    px: 4,
                    py: 2
                },
                className: `dialog-container ${status}-dialog`
            }}
        >
            {closable && (
                <IconButton
                    onClick={handleClose}
                    size="small"
                    aria-label="close"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: '#5B5B5B'
                    }}
                >
                    <i className="bi bi-x-lg" style={{ fontSize: '12px' }} />
                </IconButton>
            )}

            <DialogTitle
                sx={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    color: darkMode ? '#ffffff' : '#000000',
                    p: 0,
                    px: closable ? '44px' : 0
                }}
            >
                {heading}
            </DialogTitle>

            {status === 'loading' && (
                <Box component="img" src="/images/icons/loading-v1.svg" sx={{ width: 25, height: 24 }} />
            )}
            {status === 'success' && (
                <Box className="dialog-icon">
                    <i className="bi bi-check-circle" />
                </Box>
            )}
            {status === 'error' && (
                <Box className="dialog-icon">
                    <i className="bi bi-x-circle" />
                </Box>
            )}

            {message && (
                <DialogContentText
                    sx={{
                        m: 0,
                        fontSize: '0.95rem',
                        textAlign: 'center',
                        color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)',
                        lineHeight: 1.65,
                        whiteSpace: 'pre-line'
                    }}
                >
                    {message}
                </DialogContentText>
            )}

            {status === 'error' && actions && (
                <DialogActions sx={{ p: 0, gap: 1, justifyContent: 'center', width: '100%' }}>
                    <WcnButton onClick={handleCancel} variant="red">
                        {actions?.cancelText ? t(actions.cancelText) : t('cancel')}
                    </WcnButton>

                    <WcnButton onClick={handleConfirm} variant="primary">
                        {actions?.confirmText ? t(actions.confirmText) : t('yes')}
                    </WcnButton>
                </DialogActions>
            )}
        </Dialog>
    );
};

StatusDialog.displayName = 'StatusDialog';

export default StatusDialog;
